import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useThemeClasses } from '../hooks/useThemeClasses';

const API = process.env.REACT_APP_BACKEND_URL;

export const SEOManagement = () => {
  const theme = useThemeClasses();
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState({
    homepage_title: '',
    homepage_description: '',
    homepage_keywords: '',
    og_title: '',
    og_description: '',
    og_image: '',
    twitter_card_type: 'summary_large_image',
    twitter_site: '',
    twitter_creator: '',
    google_analytics_id: '',
    google_tag_manager_id: '',
    facebook_pixel_id: '',
    robots_txt: '',
    canonical_url: '',
    site_name: '',
    default_image: '',
    structured_data_enabled: true,
    structured_data_type: 'Organization',
    organization_name: '',
    organization_logo: '',
    organization_description: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [previewMode, setPreviewMode] = useState('google');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/admin/seo/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({ ...settings, ...response.data });
    } catch (error) {
      console.error('Failed to load SEO settings:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to load SEO settings';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/api/admin/seo/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SEO settings saved successfully!');
    } catch (error) {
      console.error('Failed to save SEO settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const getCharCount = (text, max) => {
    const length = text?.length || 0;
    const color = length > max ? 'text-red-600' : length > max * 0.9 ? 'text-yellow-600' : 'text-green-600';
    return <span className={`text-xs ${color} font-medium`}>{length}/{max}</span>;
  };

  const handleDefaultImageUpload = async (file) => {
    // Validate image size recommendation
    if (!settings.ignore_dimension_warnings) {
      const img = new Image();
      img.onload = () => {
        if (img.width < 1200 || img.height < 630) {
          toast('For best results, use an image at least 1200x630px', { icon: '⚠️' });
        }
      };
      img.src = URL.createObjectURL(file);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/api/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSettings({ ...settings, default_image: response.data.file_url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to upload image');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={theme.textSecondary}>Loading SEO settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className={`text-center ${theme.textPrimary}`}>
          <div className="text-4xl mb-4">⚠️</div>
          <h3 className="text-xl font-semibold mb-2">Failed to Load SEO Settings</h3>
          <p className={theme.textSecondary}>{error}</p>
        </div>
        <button
          onClick={loadSettings}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>🔍 SEO Management</h2>
          <p className={`text-sm ${theme.textSecondary} mt-1`}>Optimize your site for search engines and social media</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Tabs */}
      <div className={`border-b ${theme.border}`}>
        <nav className="flex space-x-8">
          {[
            { id: 'basic', label: 'Basic SEO', icon: '📝' },
            { id: 'social', label: 'Social Media', icon: '📱' },
            { id: 'analytics', label: 'Analytics', icon: '📊' },
            { id: 'advanced', label: 'Advanced', icon: '⚙️' },
            { id: 'preview', label: 'Preview', icon: '👁️' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : `border-transparent ${theme.textSecondary} ${theme.isDark ? 'hover:text-gray-200' : 'hover:text-blue-600'}`
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Basic SEO Tab */}
      {activeTab === 'basic' && (
        <div className="space-y-6">
          {/* Meta Tags */}
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>Meta Tags</h3>
              <span className={`text-xs ${theme.textMuted}`}>These appear in search results</span>
            </div>
            
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">Meta Title *</label>
                  {getCharCount(settings.homepage_title, 60)}
                </div>
                <input
                  type="text"
                  value={settings.homepage_title || ''}
                  onChange={(e) => setSettings({ ...settings, homepage_title: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="RG-3D | Premium 3D Printing Services"
                  maxLength="70"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Keep it under 60 characters. Include your main keyword.</p>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-sm font-medium">Meta Description *</label>
                  {getCharCount(settings.homepage_description, 160)}
                </div>
                <textarea
                  value={settings.homepage_description || ''}
                  onChange={(e) => setSettings({ ...settings, homepage_description: e.target.value })}
                  rows="3"
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Professional 3D printing and laser engraving services with over 350+ materials. Custom designs, fast turnaround, and premium quality."
                  maxLength="170"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Optimal length: 150-160 characters. Make it compelling to increase click-through rate.</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Focus Keywords</label>
                <input
                  type="text"
                  value={settings.homepage_keywords || ''}
                  onChange={(e) => setSettings({ ...settings, homepage_keywords: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="3D printing, laser engraving, custom designs, personalized gifts"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Separate with commas. Choose 5-10 relevant keywords.</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Canonical URL</label>
                <input
                  type="url"
                  value={settings.canonical_url || ''}
                  onChange={(e) => setSettings({ ...settings, canonical_url: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="https://www.yourdomain.com"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Your preferred domain URL. Prevents duplicate content issues.</p>
              </div>
            </div>
          </div>

          {/* Site Identity */}
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className="text-lg font-semibold mb-4">Site Identity</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Site Name</label>
                <input
                  type="text"
                  value={settings.site_name || ''}
                  onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="RG-3D"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Default Share Image</label>
                
                {/* Image Preview */}
                {settings.default_image && (
                  <div className="mb-3 relative inline-block">
                    <img 
                      src={settings.default_image} 
                      alt="Default share preview" 
                      className="w-full max-w-md h-auto rounded-lg border-2 border-gray-300 dark:border-gray-600"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, default_image: '' })}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700 shadow-lg"
                      title="Remove image"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Upload or URL Input */}
                <div className="space-y-3">
                  {/* Option to ignore dimension warnings */}
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={settings.ignore_dimension_warnings || false}
                      onChange={(e) => setSettings({ ...settings, ignore_dimension_warnings: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <span className={`text-sm ${theme.textSecondary}`}>
                      Ignore dimension warnings (recommended: 1200x630px)
                    </span>
                  </label>

                  <div className="flex gap-3">
                    <div className="flex-1">
                      <div 
                        className={`cursor-pointer px-4 py-3 border-2 border-dashed ${theme.border} rounded-lg text-center hover:border-blue-500 transition-colors`}
                        onClick={() => {
                          document.getElementById('default-image-upload').click();
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.add('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                        }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                          
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith('image/')) {
                            await handleDefaultImageUpload(file);
                          } else {
                            toast.error('Please drop an image file');
                          }
                        }}
                      >
                        <input
                          id="default-image-upload"
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              await handleDefaultImageUpload(file);
                            }
                            e.target.value = null; // Reset input to allow same file re-upload
                          }}
                          className="hidden"
                        />
                        <div className="flex flex-col items-center gap-2">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className={`text-sm ${theme.textSecondary}`}>
                            <span className="text-blue-600 dark:text-blue-400 font-medium">Click to upload</span> or drag and drop
                          </span>
                          <span className={`text-xs ${theme.textSecondary}`}>PNG, JPG, WebP up to 5MB</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className={`px-2 ${theme.bgPrimary} ${theme.textSecondary}`}>OR</span>
                    </div>
                  </div>

                  <input
                    type="url"
                    value={settings.default_image || ''}
                    onChange={(e) => setSettings({ ...settings, default_image: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="https://yourdomain.com/images/og-image.jpg"
                  />
                </div>

                <div className={`mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg`}>
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className={`text-xs ${theme.textPrimary}`}>
                      <strong>Recommended specifications:</strong>
                      <ul className="list-disc list-inside mt-1 space-y-1">
                        <li>Dimensions: 1200x630px (2:1 ratio)</li>
                        <li>Format: JPG, PNG, or WebP</li>
                        <li>File size: Under 5MB</li>
                        <li>Shows when sharing your site on Facebook, LinkedIn, Twitter, etc.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Social Media Tab */}
      {activeTab === `social` && (
        <div className="space-y-6">
          {/* Open Graph (Facebook) */}
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span className="text-2xl">📘</span> Open Graph (Facebook, LinkedIn)
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">OG Title</label>
                <input
                  type="text"
                  value={settings.og_title || settings.homepage_title || ''}
                  onChange={(e) => setSettings({ ...settings, og_title: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Leave empty to use Meta Title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">OG Description</label>
                <textarea
                  value={settings.og_description || settings.homepage_description || ''}
                  onChange={(e) => setSettings({ ...settings, og_description: e.target.value })}
                  rows="2"
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Leave empty to use Meta Description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">OG Image</label>
                
                {/* Image Preview */}
                {(settings.og_image || settings.default_image) && (
                  <div className="mb-3 relative inline-block">
                    <img 
                      src={settings.og_image || settings.default_image} 
                      alt="OG image preview" 
                      className="w-full max-w-md h-auto rounded-lg border-2 border-gray-300 dark:border-gray-600"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setSettings({ ...settings, og_image: '' })}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700 shadow-lg"
                      title="Remove image"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                )}

                {/* Upload or URL Input */}
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <label className="flex-1">
                      <div 
                        className={`cursor-pointer px-4 py-3 border-2 border-dashed ${theme.border} rounded-lg text-center hover:border-blue-500 transition-colors`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.add('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                        }}
                        onDrop={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50', 'dark:bg-blue-900/20');
                          
                          const file = e.dataTransfer.files[0];
                          if (file && file.type.startsWith('image/')) {
                            // Validate image size recommendation
                            const img = new Image();
                            img.onload = () => {
                              if (img.width < 1200 || img.height < 630) {
                                toast('For best results, use an image at least 1200x630px', { icon: '⚠️' });
                              }
                            };
                            img.src = URL.createObjectURL(file);

                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              const token = localStorage.getItem('access_token');
                              const response = await axios.post(`${API}/api/upload`, formData, {
                                headers: { 
                                  Authorization: `Bearer ${token}`,
                                  'Content-Type': 'multipart/form-data'
                                }
                              });
                              
                              setSettings({ ...settings, og_image: response.data.file_url });
                              toast.success('OG image uploaded successfully!');
                            } catch (error) {
                              console.error('Failed to upload OG image:', error);
                              toast.error('Failed to upload OG image');
                            }
                          } else {
                            toast.error('Please drop an image file');
                          }
                        }}
                      >
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              // Validate image size recommendation
                              const img = new Image();
                              img.onload = () => {
                                if (img.width < 1200 || img.height < 630) {
                                  toast('For best results, use an image at least 1200x630px', { icon: '⚠️' });
                                }
                              };
                              img.src = URL.createObjectURL(file);

                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                const token = localStorage.getItem('access_token');
                                const response = await axios.post(`${API}/api/upload`, formData, {
                                  headers: { 
                                    Authorization: `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  }
                                });
                                
                                setSettings({ ...settings, og_image: response.data.file_url });
                                toast.success('OG image uploaded successfully!');
                              } catch (error) {
                                console.error('Failed to upload OG image:', error);
                                toast.error('Failed to upload OG image');
                              }
                            }
                            e.target.value = null; // Reset input
                          }}
                          className="hidden"
                        />
                        <div className="flex flex-col items-center gap-2">
                          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className={`text-sm ${theme.textSecondary}`}>
                            <span className="text-blue-600 dark:text-blue-400 font-medium">Click to upload</span> or drag and drop
                          </span>
                          <span className={`text-xs ${theme.textSecondary}`}>PNG, JPG, WebP up to 5MB</span>
                        </div>
                      </div>
                    </label>
                  </div>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className={`px-2 ${theme.bgPrimary} ${theme.textSecondary}`}>OR</span>
                    </div>
                  </div>

                  <input
                    type="url"
                    value={settings.og_image || ''}
                    onChange={(e) => setSettings({ ...settings, og_image: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="https://yourdomain.com/images/facebook-share.jpg"
                  />
                </div>

                <p className={`text-xs ${theme.textSecondary} mt-2`}>
                  💡 Leave empty to use Default Share Image. Recommended: 1200x630px
                </p>
              </div>
            </div>
          </div>

          {/* Twitter Cards */}
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <span className="text-2xl">🐦</span> Twitter Cards
              </h3>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Card Type</label>
                <select
                  value={settings.twitter_card_type || `summary_large_image`}
                  onChange={(e) => setSettings({ ...settings, twitter_card_type: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                >
                  <option value="summary">Summary</option>
                  <option value="summary_large_image">Summary with Large Image</option>
                  <option value="app">App</option>
                  <option value="player">Player</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Twitter Site Handle</label>
                <input
                  type="text"
                  value={settings.twitter_site || ''}
                  onChange={(e) => setSettings({ ...settings, twitter_site: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="@yourbrand"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Twitter Creator Handle</label>
                <input
                  type="text"
                  value={settings.twitter_creator || ''}
                  onChange={(e) => setSettings({ ...settings, twitter_creator: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="@creator"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === `analytics` && (
        <div className="space-y-6">
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className="text-lg font-semibold mb-4">Tracking & Analytics</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Google Analytics 4 Measurement ID</label>
                <input
                  type="text"
                  value={settings.google_analytics_id || ''}
                  onChange={(e) => setSettings({ ...settings, google_analytics_id: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="G-XXXXXXXXXX"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Find this in your GA4 property settings</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Google Tag Manager ID</label>
                <input
                  type="text"
                  value={settings.google_tag_manager_id || ''}
                  onChange={(e) => setSettings({ ...settings, google_tag_manager_id: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="GTM-XXXXXXX"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Manages all your tracking tags from one place</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Facebook Pixel ID</label>
                <input
                  type="text"
                  value={settings.facebook_pixel_id || ''}
                  onChange={(e) => setSettings({ ...settings, facebook_pixel_id: e.target.value })}
                  className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="123456789012345"
                />
                <p className={`text-xs ${theme.textSecondary} mt-1`}>💡 Track conversions and optimize Facebook ads</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Advanced Tab */}
      {activeTab === `advanced` && (
        <div className="space-y-6">
          {/* Structured Data */}
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Structured Data (Schema.org)</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.structured_data_enabled || false}
                  onChange={(e) => setSettings({ ...settings, structured_data_enabled: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-sm">Enable</span>
              </label>
            </div>
            
            {settings.structured_data_enabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Schema Type</label>
                  <select
                    value={settings.structured_data_type || `Organization`}
                    onChange={(e) => setSettings({ ...settings, structured_data_type: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  >
                    <option value="Organization">Organization</option>
                    <option value="LocalBusiness">Local Business</option>
                    <option value="Store">Store</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Organization Name</label>
                  <input
                    type="text"
                    value={settings.organization_name || ''}
                    onChange={(e) => setSettings({ ...settings, organization_name: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="RG-3D"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Organization Logo URL</label>
                  <input
                    type="url"
                    value={settings.organization_logo || ''}
                    onChange={(e) => setSettings({ ...settings, organization_logo: e.target.value })}
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="https://yourdomain.com/logo.png"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Organization Description</label>
                  <textarea
                    value={settings.organization_description || ''}
                    onChange={(e) => setSettings({ ...settings, organization_description: e.target.value })}
                    rows="3"
                    className={`w-full px-3 py-2 border ${theme.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Brief description of your organization"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Robots.txt */}
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className="text-lg font-semibold mb-4">Robots.txt</h3>
            <textarea
              value={settings.robots_txt || `User-agent: *\nAllow: /\nSitemap: /api/seo/sitemap.xml`}
              onChange={(e) => setSettings({ ...settings, robots_txt: e.target.value })}
              rows="8"
              className={`w-full px-3 py-2 border ${theme.border} rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              placeholder="User-agent: *&#10;Allow: /"
            />
            <div className="mt-3 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800">💡 <strong>Tip:</strong> This controls how search engines crawl your site. Be careful with "Disallow" rules.</p>
            </div>
          </div>

          {/* Sitemap */}
          <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
            <h3 className="text-lg font-semibold mb-4">XML Sitemap</h3>
            <div className={`flex items-center justify-between p-4 ${theme.bgSecondary} rounded-lg`}>
              <div>
                <p className={`text-sm font-medium ${theme.textPrimary}`}>Sitemap is automatically generated</p>
                <p className={`text-xs ${theme.textSecondary} mt-1`}>URL: {window.location.origin}/api/seo/sitemap.xml</p>
              </div>
              <a
                href={`${API}/api/seo/sitemap.xml`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                View Sitemap
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Preview Tab */}
      {activeTab === `preview` && (
        <div className="space-y-6">
          {/* Preview Mode Selector */}
          <div className={`flex gap-2 ${theme.card} p-2 rounded-lg shadow-sm border ${theme.border} w-fit`}>
            {['google', 'facebook', 'twitter'].map((mode) => (
              <button
                key={mode}
                onClick={() => setPreviewMode(mode)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  previewMode === mode
                    ? 'bg-blue-600 text-white'
                    : `${theme.bgSecondary} ${theme.textPrimary} hover:bg-gray-200`
                }`}
              >
                {mode === 'google' && '🔍 Google'}
                {mode === 'facebook' && '📘 Facebook'}
                {mode === 'twitter' && '🐦 Twitter'}
              </button>
            ))}
          </div>

          {/* Google Preview */}
          {previewMode === 'google' && (
            <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
              <h3 className="text-lg font-semibold mb-4">Google Search Preview</h3>
              <div className={`border ${theme.border} rounded-lg p-4 ${theme.card}`}>
                <div className={`text-sm ${theme.textSecondary} mb-1`}>{settings.canonical_url || 'https://www.yourdomain.com'}</div>
                <div className="text-xl text-blue-600 hover:underline cursor-pointer mb-1">
                  {settings.homepage_title || 'Your Page Title'}
                </div>
                <div className={`text-sm ${theme.textSecondary}`}>
                  {settings.homepage_description || 'Your meta description will appear here. Make it compelling to encourage users to click through to your site.'}
                </div>
              </div>
              <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Actual search results may vary based on user location, search query, and Google's algorithms.
                </p>
              </div>
            </div>
          )}

          {/* Facebook Preview */}
          {previewMode === 'facebook' && (
            <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
              <h3 className="text-lg font-semibold mb-4">Facebook Share Preview</h3>
              <div className={`border ${theme.border} rounded-lg overflow-hidden ${theme.card} max-w-lg`}>
                {(settings.og_image || settings.default_image) && (
                  <img 
                    src={settings.og_image || settings.default_image} 
                    alt="Preview" 
                    className="w-full h-64 object-cover bg-gray-200"
                  />
                )}
                <div className="p-3">
                  <div className={`text-xs ${theme.textSecondary} mb-1 uppercase`}>{settings.canonical_url || `yourdomain.com`}</div>
                  <div className={`font-semibold ${theme.textPrimary} mb-1`}>
                    {settings.og_title || settings.homepage_title || `Your Page Title`}
                  </div>
                  <div className={`text-sm ${theme.textSecondary}`}>
                    {settings.og_description || settings.homepage_description || 'Your description'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Twitter Preview */}
          {previewMode === 'twitter' && (
            <div className={`${theme.card} p-6 rounded-lg shadow-sm border ${theme.border}`}>
              <h3 className="text-lg font-semibold mb-4">Twitter Card Preview</h3>
              <div className={`border ${theme.border} rounded-2xl overflow-hidden ${theme.card} max-w-lg`}>
                {(settings.og_image || settings.default_image) && (
                  <img 
                    src={settings.og_image || settings.default_image} 
                    alt="Preview" 
                    className="w-full h-64 object-cover bg-gray-200"
                  />
                )}
                <div className="p-3">
                  <div className={`font-semibold ${theme.textPrimary} mb-1`}>
                    {settings.og_title || settings.homepage_title || `Your Page Title`}
                  </div>
                  <div className={`text-sm ${theme.textSecondary} mb-2`}>
                    {settings.og_description || settings.homepage_description || `Your description`}
                  </div>
                  <div className={`text-xs ${theme.textSecondary}`}>{settings.canonical_url || `yourdomain.com`}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Save Button at Bottom */}
      <div className={`flex justify-end pt-4 border-t ${theme.border}`}>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors font-medium"
        >
          {saving ? 'Saving...' : 'Save All Settings'}
        </button>
      </div>
    </div>
  );
};